import type { JobStatus } from "@zenforge/shared";
import { useEffect, useState } from "react";

const BADGE_CLASS: Record<JobStatus, string> = {
  PENDING: "pending",
  PROCESSING: "processing",
  BATCHED: "processing",
  GENERATED: "generated",
  IN_REVIEW: "review",
  APPROVED: "approved",
  FAILED: "failed",
  REGENERATE: "processing",
  CANCELLED: "pending",
  PAUSED: "paused",
};

const BADGE_LABEL: Record<JobStatus, string> = {
  PENDING: "Pending",
  PROCESSING: "Processing",
  BATCHED: "In Batch",
  GENERATED: "Generated",
  IN_REVIEW: "In Review",
  APPROVED: "Approved",
  FAILED: "Failed",
  REGENERATE: "Regenerate",
  CANCELLED: "Cancelled",
  PAUSED: "Paused",
};

export function StatusBadge({ status }: { status: JobStatus }) {
  return <span className={`badge ${BADGE_CLASS[status]}`}>{BADGE_LABEL[status]}</span>;
}

/** Inline spinner for a button or row mid-request. Sized off the current font
 *  size (1em) so it drops into a `.btn` or table cell without its own prop. */
export function Spinner({ label }: { label?: string }) {
  return (
    <span className="spinner" role="status" aria-label={label ?? "Loading"}>
      <span className="spinner-circle" />
    </span>
  );
}

/**
 * A centered spinner over a lightly dimmed, full-viewport backdrop — for a
 * page's own load (e.g. a `useApi()` call with no data yet) or a mutation
 * important enough to hold the whole screen rather than just one button.
 *
 * Opt-in per call site (`{loading && <FullScreenLoader />}`) rather than
 * wired to every request automatically: most mutations are already covered
 * by `ActionButton`'s inline spinner, and this is for the cases that want
 * the heavier "block the screen" treatment instead. Deliberately lighter
 * than `.modal-backdrop` (no blur, low opacity) since it is signalling
 * "something is happening", not demanding focus the way a modal does.
 */
export function FullScreenLoader({ label }: { label?: string }) {
  return (
    <div className="fullscreen-loader" role="status" aria-live="polite" aria-label={label ?? "Loading"}>
      <span className="fullscreen-loader-spinner" />
      {label && <span className="fullscreen-loader-label">{label}</span>}
    </div>
  );
}

/**
 * Dims its children and overlays a small spinner while `loading` — for a
 * `useApi()` list that already has a page of rows on screen and is
 * refetching under it (a filter change, a page turn), where `useApi`
 * deliberately keeps the stale rows mounted rather than clearing them (see
 * its own comment: nulling data on a polled endpoint would blank a working
 * table on every refresh). Without this there is no visual difference
 * between "filtered" and "still filtering" until the new rows swap in.
 *
 * Distinct from `FullScreenLoader`, which is for the *first* load, when
 * there is nothing underneath yet to dim.
 *
 * The dim/spinner itself is debounced by REFETCH_OVERLAY_DELAY_MS: a refetch
 * that resolves quickly (the common case on a fast connection, or a page
 * that refreshes itself on an interval) never shows it at all, so the table
 * doesn't visibly flash dim-then-clear on every fast round trip — only a
 * refetch that's actually taking a moment gets the "this is loading"
 * treatment. `loading` flipping true and immediately back to false before
 * the timer fires is the exact case this exists to hide.
 */
const REFETCH_OVERLAY_DELAY_MS = 300;

export function RefetchOverlay({
  loading,
  children,
}: {
  loading: boolean;
  children: React.ReactNode;
}) {
  const [showOverlay, setShowOverlay] = useState(false);

  useEffect(() => {
    if (!loading) {
      setShowOverlay(false);
      return;
    }
    const timer = setTimeout(() => setShowOverlay(true), REFETCH_OVERLAY_DELAY_MS);
    return () => clearTimeout(timer);
  }, [loading]);

  return (
    <div className="refetch-overlay" aria-busy={loading}>
      <div className={showOverlay ? "refetch-overlay-content is-dimmed" : "refetch-overlay-content"}>
        {children}
      </div>
      {showOverlay && (
        <div className="refetch-overlay-spinner" role="status" aria-label="Refreshing">
          <span className="fullscreen-loader-spinner" />
        </div>
      )}
    </div>
  );
}

/**
 * A `.btn` that shows a spinner and disables itself while `pending`, instead
 * of every call site hand-rolling `disabled={pending}` plus a conditional
 * icon. `children` is what the button reads once idle; the label during the
 * request defaults to the same text so the button doesn't jump in width.
 */
export function ActionButton({
  pending,
  onClick,
  children,
  className = "",
  disabled,
  ...rest
}: {
  pending: boolean;
  /** Takes the click event too — several call sites sit inside a row that
   *  has its own onClick (e.g. expand/collapse) and need to stop it from
   *  also firing. */
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  children: React.ReactNode;
  disabled?: boolean;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onClick" | "children" | "disabled">) {
  return (
    <button
      type="button"
      className={`btn ${className}`.trim()}
      onClick={onClick}
      disabled={pending || disabled}
      aria-busy={pending}
      {...rest}
    >
      {pending && <Spinner />}
      {children}
    </button>
  );
}

export function Notice({
  kind,
  children,
}: {
  kind: "info" | "warn" | "error";
  children: React.ReactNode;
}) {
  return <div className={`notice ${kind}`}>{children}</div>;
}

/** Shown on every screen still backed by mock data, so nobody mistakes it for
 *  a working queue. */
export function MockBanner({ milestone }: { milestone: number }) {
  return (
    <Notice kind="warn">
      <strong>Mock data.</strong> This screen is not connected to the database yet — the real API
      lands in Milestone {milestone}. Nothing here reflects actual jobs or content.
    </Notice>
  );
}

export function useToast() {
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (message === null) return;
    const timer = setTimeout(() => setMessage(null), 2200);
    return () => clearTimeout(timer);
  }, [message]);

  const toast = (text: string) => setMessage(text);
  const element = message === null ? null : <div className="toast">{message}</div>;

  return { toast, element };
}

export function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return String(n);
}

export function formatCost(cost: number | string | null | undefined): string {
  if (cost === null || cost === undefined || cost === "") return "—";
  const num = typeof cost === "number" ? cost : Number(cost);
  if (isNaN(num)) return "—";
  return `$${num.toFixed(4)}`;
}

/**
 * Provider call duration. Null means "not measured" — rows generated before
 * migration 018 have no value, and showing 0.0s for those would read as an
 * instant call rather than a missing one.
 *
 * Sub-minute durations keep one decimal (the difference between 3.2s and 8.7s
 * matters when comparing models); past a minute the seconds are what matter
 * less, so it switches to m/s, and past an hour to h/m/s.
 *
 * Rounding is done once, on the total, before the units are split out.
 * Rounding each unit independently lets 59.6s in the seconds place render as
 * "3m 60s" — a value that never reads as a real time.
 */
export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return "—";
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;

  const totalSeconds = Math.round(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  return `${minutes}m ${seconds}s`;
}

/**
 * A model picker that's never limited to its presets: pick one from the
 * list, or switch to typing a name that isn't in it yet (a dated snapshot,
 * or a model added after this list was last updated). Starts in whichever
 * mode fits the current value, so loading a collection with an already-custom
 * model doesn't silently show the wrong thing.
 */
export function ModelSelect({
  value,
  onChange,
  models,
}: {
  value: string;
  onChange: (model: string) => void;
  models: string[];
}) {
  const [customMode, setCustomMode] = useState(value !== "" && !models.includes(value));

  if (customMode) {
    return (
      <div className="row" style={{ gap: 8 }}>
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="e.g. gpt-4.1-2025-04-14"
          style={{ flex: 1 }}
        />
        <button type="button" className="btn mini" onClick={() => setCustomMode(false)}>
          Choose from list
        </button>
      </div>
    );
  }

  return (
    <select
      value={models.includes(value) ? value : ""}
      onChange={(e) => {
        if (e.target.value === "__custom__") {
          setCustomMode(true);
          return;
        }
        onChange(e.target.value);
      }}
    >
      <option value="">Select a model…</option>
      {models.map((m) => (
        <option key={m} value={m}>
          {m}
        </option>
      ))}
      <option value="__custom__">Custom / other…</option>
    </select>
  );
}

/**
 * Compact token counts: 16854 -> "16.9K".
 *
 * The queue shows tokens on every row, where full precision ("16,854") is
 * both wider and less readable at a glance. Exact figures stay available in
 * the job detail modal.
 */
export function compactTokens(n: number | string | null | undefined): string {
  if (n === null || n === undefined || n === "") return "—";
  const num = typeof n === "number" ? n : Number(n);
  if (isNaN(num)) return "—";
  if (num < 1000) return String(num);
  if (num < 1_000_000) return `${(num / 1000).toFixed(1).replace(/\.0$/, "")}K`;
  return `${(num / 1_000_000).toFixed(2).replace(/\.00$/, "")}M`;
}

/**
 * Cost at a precision that stays meaningful without reading like a billing
 * ledger: "$1.41", "$0.115", "$0.0004".
 *
 * formatCost's fixed four decimals turns every real figure into "$0.1150" and
 * every large one into "$12.3400"; this scales the precision to the value.
 */
export function compactCost(cost: number | string | null | undefined): string {
  if (cost === null || cost === undefined || cost === "") return "—";
  const num = typeof cost === "number" ? cost : Number(cost);
  if (isNaN(num)) return "—";
  if (num === 0) return "$0";
  if (num >= 1) return `$${num.toFixed(2)}`;
  if (num >= 0.01) return `$${num.toFixed(3)}`;
  // Small enough that three decimals would round it to nothing.
  return `$${num.toFixed(4)}`;
}

/** Visual family for a status — the queue groups nine statuses into six
 *  colours, because a distinct colour per status is noise, not information. */
export type StatusTone = "pending" | "active" | "done" | "failed" | "review" | "paused";

const STATUS_TONE: Record<JobStatus, StatusTone> = {
  PENDING: "pending",
  PROCESSING: "active",
  BATCHED: "active",
  GENERATED: "done",
  IN_REVIEW: "review",
  APPROVED: "done",
  FAILED: "failed",
  REGENERATE: "active",
  CANCELLED: "pending",
  PAUSED: "paused",
};

/** Operator-facing wording. "In Batch" rather than the provider's
 *  status=in_progress, "Retrying" rather than REGENERATE. */
const STATUS_TEXT: Record<JobStatus, string> = {
  PENDING: "Queued",
  PROCESSING: "Processing",
  BATCHED: "In Batch",
  GENERATED: "Completed",
  IN_REVIEW: "In Review",
  APPROVED: "Approved",
  FAILED: "Failed",
  REGENERATE: "Retrying",
  CANCELLED: "Cancelled",
  PAUSED: "Paused",
};

export function statusTone(status: JobStatus): StatusTone {
  return STATUS_TONE[status];
}

export function statusText(status: JobStatus): string {
  return STATUS_TEXT[status];
}

/** A status as a dot plus its name. Quieter than StatusBadge, which stays in
 *  use on screens that show one status rather than dozens. */
export function StatusDot({ status, label }: { status: JobStatus; label?: string }) {
  return (
    <span className={`status-dot is-${statusTone(status)}`}>{label ?? statusText(status)}</span>
  );
}
